<script>
import axios from '@/utils/axios';

const DEFAULT_FORM = () => ({
  name: '',
  type: 'offer_letter',
  content: '',
});

export default {
  inject: ['setLoading'],
  emits: ['updated'],
  data() {
    return {
      mode: 'create',      // 'create' | 'edit'
      editId: null,
      form: DEFAULT_FORM(),
      errors: {},
      placeholders: {},
      typeOptions: ['offer_letter', 'appointment_letter', 'warning_letter', 'experience_letter', 'other'],
    };
  },
  computed: {
    isEdit() {
      return this.mode === 'edit';
    },
    placeholderGroups() {
      return Object.keys(this.placeholders || {});
    },
  },
  mounted() {
    this.fetchPlaceholders();
  },
  methods: {
    async fetchPlaceholders() {
      try {
        const res = await axios.get('/hrms/hrms-documents/placeholders');
        this.placeholders = res.data.data || {};
      } catch (error) {
        console.log(error.message);
      }
    },

    groupLabel(g) {
      return g.charAt(0).toUpperCase() + g.slice(1);
    },

    insertPlaceholder(tag) {
      const textarea = this.$refs.contentArea;
      if (!textarea) {
        this.form.content += tag;
        return;
      }
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const before = this.form.content.slice(0, start);
      const after = this.form.content.slice(end);
      this.form.content = before + tag + after;
      this.$nextTick(() => {
        textarea.focus();
        const pos = start + tag.length;
        textarea.setSelectionRange(pos, pos);
      });
    },

    // ----- open in CREATE mode -----
    create() {
      this.mode = 'create';
      this.editId = null;
      this.form = DEFAULT_FORM();
      this.errors = {};
    },

    // ----- open in EDIT mode -----
    edit(row) {
      this.mode = 'edit';
      this.editId = row.id;
      this.form = {
        name: row.name || '',
        type: row.type || 'offer_letter',
        content: row.content || '',
      };
      this.errors = {};
    },

    async save() {
      this.errors = {};
      this.setLoading(true);
      try {
        let response;
        if (this.isEdit) {
          response = await axios.put(`/hrms/hrms-documents/${this.editId}`, this.form);
        } else {
          response = await axios.post('/hrms/hrms-documents', this.form);
        }
        this.$swalToast(response.data.message);
        this.$emit('updated');
        this.$closeModal('hrm_document_modal');
      } catch (error) {
        if (error.type === 'validation') {
          this.errors = error.errors;
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },
  },
};
</script>

<template>
  <div class="modal fade" id="hrm_document_modal">
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">{{ isEdit ? 'Edit Document Template' : 'Add Document Template' }}</h4>
          <button type="button" class="btn-close custom-btn-close" data-bs-dismiss="modal" aria-label="Close">
            <i class="ti ti-x"></i>
          </button>
        </div>
        <form @submit.prevent="save">
          <div class="modal-body pb-0">
            <div class="row">

              <!-- Left: form fields -->
              <div class="col-md-8">
                <div class="row">
                  <div class="col-md-7 mb-3">
                    <label class="form-label">Template Name <span class="text-danger">*</span></label>
                    <input type="text" class="form-control" v-model="form.name" placeholder="e.g. Standard Offer Letter" />
                    <div v-if="errors.name" class="text-danger small mt-1">{{ errors.name[0] }}</div>
                  </div>
                  <div class="col-md-5 mb-3">
                    <label class="form-label">Type <span class="text-danger">*</span></label>
                    <select class="form-select" v-model="form.type">
                      <option v-for="t in typeOptions" :key="t" :value="t">
                        {{ t.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()) }}
                      </option>
                    </select>
                    <div v-if="errors.type" class="text-danger small mt-1">{{ errors.type[0] }}</div>
                  </div>
                </div>

                <div class="mb-3">
                  <label class="form-label">Content <span class="text-danger">*</span></label>
                  <textarea
                    ref="contentArea"
                    class="form-control"
                    rows="14"
                    style="font-family: 'Courier New', monospace; font-size:12.5px;"
                    v-model="form.content"
                    placeholder="Write the document content here. Click placeholders on the right to insert them, e.g. {{employee_name}}"
                  ></textarea>
                  <div v-if="errors.content" class="text-danger small mt-1">{{ errors.content[0] }}</div>
                </div>
              </div>

              <!-- Right: placeholder reference -->
              <div class="col-md-4">
                <label class="form-label">Insert Placeholder</label>
                <div class="doc-ph-panel">
                  <div v-for="group in placeholderGroups" :key="group" class="doc-ph-group">
                    <div class="doc-ph-group-title">{{ groupLabel(group) }}</div>
                    <div class="doc-ph-chips">
                      <button
                        v-for="ph in placeholders[group]"
                        :key="ph"
                        type="button"
                        class="doc-ph-chip"
                        @click="insertPlaceholder(ph)"
                      >
                        {{ ph }}
                      </button>
                    </div>
                  </div>
                  <div v-if="!placeholderGroups.length" class="text-muted" style="font-size:11.5px;">
                    Loading placeholders...
                  </div>
                </div>
              </div>

            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal">Cancel</button>
            <button type="submit" class="btn btn-primary">{{ isEdit ? 'Update' : 'Save' }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<style scoped>
.doc-ph-panel {
  background: #f8fafc; border: 1px solid #eef2f7; border-radius: 10px;
  padding: 12px; max-height: 380px; overflow-y: auto;
}
.doc-ph-group { margin-bottom: 14px; }
.doc-ph-group:last-child { margin-bottom: 0; }
.doc-ph-group-title {
  font-size: 10.5px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .4px; color: #94a3b8; margin-bottom: 6px;
}
.doc-ph-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.doc-ph-chip {
  font-size: 11px; font-family: 'Courier New', monospace;
  background: #eef2ff; color: #4f46e5; border: 1px solid #e0e7ff;
  border-radius: 6px; padding: 4px 8px; cursor: pointer;
  transition: all .12s ease;
}
.doc-ph-chip:hover { background: #4f46e5; color: #fff; }
</style>